import { Button, List, Popover, Switch, Dropdown, Menu } from 'antd';
import React, { useEffect, useState } from 'react';
import { withIsMobile } from 'treeline-react-core-components/WindowSizeProvider';
import handlePlural from '../../util/handlePlural';
import { connectToDownloads } from '../Downloads/containers/DownloadsDataStore';
import { connectToRequests } from '../Requests/containers/RequestsDataStore';
import { connectToReviews } from '../Reviews/containers/ReviewsDataStore';
import TitleActivityTile from '../Title/TitleActivityTile';
import combineConnectors from '../util/combineConnectors';
import TitleFilter from './Filter/TitleFilter';
import TitleSearch from './Search/TitleSearch';
import TitleSortAttribute from './Sort/TitleSortAttribute';
import TitleListType from './TitleListType';
import { TitleListActionOptions } from './TitleListActionOptions';
import PropTypes from 'prop-types';
import { Translate } from '@above-the-treeline/localization';
import './tiledTitleList.less';

export const TiledTitleList = ({
	skus,
	titles,
	isLoading,
	listKey,
	isMobile,
	titleListType,
	requestsDataStore,
	downloadsDataStore,
	reviewsDataStore,
}) => {
	const [filterListKey, setFilterListKey] = useState(null);
	const [isFiltered, setIsFiltered] = useState(false);
	const [filteredSkus, setFilteredSkus] = useState([]);
	const [isSearchOn, setIsSearchOn] = useState(false);
	const [searchedSkus, setSearchedSkus] = useState([]);
	const [titleSortAttribute, setTitleSortAttribute] = useState(
		TitleSortAttribute.OnSaleDate,
	);
	const [isSortDirectionAsc, setIsSortDirectionAsc] = useState(false);
	const [selectedSkus, setSelectedSkus] = useState([]);
	const isMobileSize = isMobile();

	const renderedSkus = isSearchOn
		? searchedSkus
		: isFiltered && listKey === filterListKey
		? filteredSkus
		: skus;

	useEffect(() => {
		if (titleListType) {
			setTitleSortAttribute(titleListType.defaultSortAttribute);
			setIsSortDirectionAsc(titleListType.isSortDirectionAsc);
		}
	}, []);

	const getSearchData = (searchedSkus, searchCharacters) => {
		if (
			searchCharacters > 0 &&
			searchedSkus &&
			searchedSkus.length > 0 &&
			searchedSkus.length !== skus.length
		) {
			setIsSearchOn(true);
			setSearchedSkus(searchedSkus);
		} else {
			setIsSearchOn(false);
			setSearchedSkus([]);
		}
	};

	const getData = (searchedSkus, key) => {
		if (
			key === listKey &&
			searchedSkus &&
			searchedSkus.length > 0 &&
			searchedSkus.length !== skus.length
		) {
			setIsFiltered(true);
			setFilteredSkus(searchedSkus);
			setFilterListKey(key);
		} else {
			setIsFiltered(false);
			setFilteredSkus([]);
			setFilterListKey(listKey);
		}
	};

	const renderTitle = (sku, titles) => {
		const { requests } = requestsDataStore.state;
		const { downloads } = downloadsDataStore.state;
		const { reviews } = reviewsDataStore.state;
		const request = requests && requests.filter(rq => rq.sku === sku)[0];

		const skuDownload = downloads && downloads.filter(dn => dn.sku === sku);
		const download =
			skuDownload && skuDownload.length > 0 && skuDownload[0];

		const skuReview = reviews && reviews.filter(rv => rv.sku === sku);
		const review = skuReview && skuReview.length > 0 && skuReview[0];

		const skuTitle = titles.filter(dn => dn.sku === sku);
		const title = skuTitle && skuTitle.length > 0 && skuTitle[0];

		return (
			<div className="title-activity-tile" key={title.sku}>
				<TitleActivityTile
					request={request}
					download={download}
					review={review}
					title={title}
					logSelectedTitles={logSelectedTitles}
					isSelected={
						selectedSkus.filter(sk => sk === title.sku).length > 0
					}
					titleListType={titleListType}
				/>
			</div>
		);
	};

	const renderFilterLink = renderedSkus => {
		if (listKey !== filterListKey) {
			setIsFiltered(false);
			setFilteredSkus([]);
			setFilterListKey(listKey);
		}
		const unfilteredTitles = titles.filter(function(item) {
			return skus.includes(item.sku);
		});
		const filteredTitles = isFiltered
			? titles.filter(function(item) {
					return filteredSkus.includes(item.sku);
			  })
			: unfilteredTitles;
		const filteredProducts = filteredTitles.map(function(item) {
			return item.product;
		});
		return (
			<div className="top-menu">
				<div className="column bold total-titles-header">
					{handlePlural('Title', skus.length, false)}
				</div>
				<div className={!isMobileSize ? 'column-spaced' : null}>
					<TitleSearch
						titles={filteredProducts}
						sendSearchData={getSearchData}
					/>
				</div>
				<div
					className={`flex-container ${
						isMobileSize
							? 'flex-column mobile-title-buttons'
							: 'column-right'
					}`}>
					{renderActionHeader(renderedSkus)}
					{renderFilterHeader(unfilteredTitles)}
					{renderSortHeader()}
					<div className="clear" />
				</div>
				<div className="clear" />
			</div>
		);
	};

	const renderActionHeader = renderedSkus => {
		const renderActionTitleText = () => (
			<span
				className={`${
					!isMobileSize ? 'column ' : ''
				}title-filter-text`}>
				Action{' '}
				{selectedSkus.length === 0
					? 'All Titles'
					: handlePlural('Title', selectedSkus.length, false)}
			</span>
		);
		return (
			<div className="column relative">
				{!isMobileSize && renderActionTitleText()}
				<div className={!isMobileSize ? 'column-spaced' : null}>
					<Dropdown
						overlay={renderActionOptions(renderedSkus)}
						placement="bottomRight"
						trigger={['click', 'hover']}>
						<Button shape={!isMobileSize ? 'circle' : null} block>
							{isMobileSize && renderActionTitleText()}
							<div className="icon-active title-search-icon" />
						</Button>
					</Dropdown>
				</div>
				<div className="clear" />
			</div>
		);
	};

	const renderFilterHeader = unfilteredTitles => {
		const filterText = (
			<span
				className={`${
					!isMobileSize ? 'column column-spaced ' : ''
				}title-filter-text`}>
				{isFiltered
					? handlePlural('Filtered Title', filteredSkus.length, false)
					: 'Filter'}
			</span>
		);

		const unfilteredProducts = unfilteredTitles.map(item => item.product);

		return (
			<TitleFilter
				titles={unfilteredProducts}
				sendData={getData}
				listKey={listKey}>
				<div className={isFiltered ? 'filters-on' : null}>
					{!isMobileSize ? filterText : null}
					<div className={!isMobileSize ? 'column-spaced' : null}>
						<Button shape={!isMobileSize ? 'circle' : null} block>
							{isMobileSize ? filterText : null}
							<div
								className={`${
									isFiltered ? 'filters-on ' : ''
								}icon-filter title-filter-icon`}
							/>
						</Button>
					</div>
					<div className="clear" />
				</div>
			</TitleFilter>
		);
	};

	const renderActionOptionLabel = option => {
		const { classNames, label } = option;
		return (
			<>
				<div className={`column ${classNames}`}></div>
				<div className="column-spaced acc-font bold">
					<Translate id={label} />
				</div>
				<div className="clear" />
			</>
		);
	};

	const renderOneActionOption = (option, skus) => {
		const { drawer: Drawer } = option;
		return (
			<Menu.Item>
				<Drawer skus={skus}>{renderActionOptionLabel(option)}</Drawer>
			</Menu.Item>
		);
	};

	const renderActionOptions = renderedSkus => {
		const actionSkus =
			selectedSkus.length === 0 ? renderedSkus : selectedSkus;
		return (
			<Menu className="title-action-menu">
				{renderOneActionOption(
					TitleListActionOptions.AddToCollaborativeList,
					actionSkus,
				)}
				{renderOneActionOption(
					TitleListActionOptions.AddToShelf,
					actionSkus,
				)}
				{renderOneActionOption(
					TitleListActionOptions.ShareOrExport,
					actionSkus,
				)}
				{selectedSkus.length > 0 && (
					<Menu.Item>
						<div onClick={clearTitleSelections}>
							{renderActionOptionLabel(
								TitleListActionOptions.ClearTitleSelections,
							)}
						</div>
					</Menu.Item>
				)}
			</Menu>
		);
	};

	const clearTitleSelections = () => {
		setSelectedSkus([]);
	};

	const renderSortHeader = () => {
		const actionTitleText = (
			<span
				className={`${
					!isMobileSize ? 'column ' : ''
				}title-filter-text`}>
				Sort
			</span>
		);
		return (
			<div className={!isMobileSize ? 'column-spaced relative' : null}>
				{!isMobileSize && actionTitleText}
				<div className={!isMobileSize ? 'column-spaced' : null}>
					<Popover
						trigger="click"
						placement="bottomRight"
						content={renderSortOptions()}>
						<Button shape={!isMobileSize ? 'circle' : null} block>
							{isMobileSize && actionTitleText}
							<div className="icon-caret-up-down title-search-icon" />
						</Button>
					</Popover>
				</div>
				<div className="clear" />
			</div>
		);
	};

	const renderSortOptions = () => {
		const getSortOptions = () => {
			const sortOptions = [
				TitleSortAttribute.OnSaleDate,
				TitleSortAttribute.PubDate,
				TitleSortAttribute.DigitalUpdatedDate,
				TitleSortAttribute.Supplier,
				TitleSortAttribute.Category,
				TitleSortAttribute.Author,
				TitleSortAttribute.Pages,
				TitleSortAttribute.CommunityReviews,
				TitleSortAttribute.CommunityRating,
			];
			titleListType === TitleListType.Catalog &&
				sortOptions.unshift(TitleSortAttribute.Sequence);
			titleListType === TitleListType.CollaborativeList &&
				sortOptions.unshift(TitleSortAttribute.NetVotes);
			return sortOptions;
		};
		return (
			<div className="top-menu-sort-popover">
				<div className="column sort-toggle">Sort Direction</div>
				<div className="column-right sort-switch">
					<Switch
						checkedChildren="Asc"
						unCheckedChildren="Desc"
						defaultChecked={isSortDirectionAsc}
						onChange={e => changeSortDirection(e)}
					/>
				</div>
				<div className="clear" />
				<div className="sorted-by-header">Sorted By</div>
				{getSortOptions().map(sortOption =>
					renderSortOption(sortOption),
				)}
			</div>
		);
	};

	const renderSortOption = thisTitleSortAttribute => {
		return (
			<div
				key={`sort-${thisTitleSortAttribute.attribute}`}
				className="clickable acc-font"
				onClick={() => changeSortAttribute(thisTitleSortAttribute)}>
				<span
					className={
						titleSortAttribute === thisTitleSortAttribute
							? 'selected-search-attribute'
							: null
					}>
					{thisTitleSortAttribute.label}
				</span>
			</div>
		);
	};

	const changeSortDirection = e => {
		setIsSortDirectionAsc(e);
	};

	const changeSortAttribute = titleSortAttribute => {
		setTitleSortAttribute(titleSortAttribute);
	};

	const sortTitles = renderedSkus => {
		const renderedTitles = titles.filter(function(item) {
			return renderedSkus.includes(item.sku);
		});
		var sortedTitles = {};
		if (titleSortAttribute.type === 'string') {
			sortedTitles = sortString(
				renderedTitles,
				titleSortAttribute.attribute,
				isSortDirectionAsc,
			);
		} else if (titleSortAttribute.type === 'number') {
			sortedTitles = sortNumber(
				renderedTitles,
				titleSortAttribute.attribute,
				isSortDirectionAsc,
			);
		} else if (titleSortAttribute.type === 'date') {
			sortedTitles = sortDate(
				renderedTitles,
				titleSortAttribute.attribute,
				titleSortAttribute.secondaryAttribute,
				isSortDirectionAsc,
			);
		}
		return sortedTitles.map(function(item) {
			return item.sku;
		});
	};

	const sortNumber = (titles, attribute, isSortDirectionAsc) => {
		return !isSortDirectionAsc === true
			? titles.sort((a, b) => b.product[attribute] - a.product[attribute])
			: titles.sort(
					(a, b) => a.product[attribute] - b.product[attribute],
			  );
	};

	const sortString = (titles, attribute, isSortDirectionAsc) => {
		return !isSortDirectionAsc === true
			? titles.sort((a, b) =>
					('' + b.product[attribute]).localeCompare(
						a.product[attribute],
					),
			  )
			: titles.sort((a, b) =>
					('' + a.product[attribute]).localeCompare(
						b.product[attribute],
					),
			  );
	};

	const sortDate = (
		titles,
		attribute,
		secondaryAttribute,
		isSortDirectionAsc,
	) => {
		return !isSortDirectionAsc === true
			? titles.sort(
					(a, b) =>
						new Date(
							b.product[attribute]
								? b.product[attribute]
								: b.product[secondaryAttribute]
								? b.product[secondaryAttribute]
								: '1/1/1910',
						) -
						new Date(
							a.product[attribute]
								? a.product[attribute]
								: a.product[secondaryAttribute]
								? a.product[secondaryAttribute]
								: '1/1/1910',
						),
			  )
			: titles.sort(
					(a, b) =>
						new Date(
							a.product[attribute]
								? a.product[attribute]
								: a.product[secondaryAttribute]
								? a.product[secondaryAttribute]
								: '1/1/1910',
						) -
						new Date(
							b.product[attribute]
								? b.product[attribute]
								: b.product[secondaryAttribute]
								? b.product[secondaryAttribute]
								: '1/1/1910',
						),
			  );
	};

	const logSelectedTitles = (sku, isSelected) => {
		if (isSelected) {
			setSelectedSkus(s => [...s, sku]);
		} else {
			var newSelectedSkus = selectedSkus.filter(sk => sk !== sku);
			setSelectedSkus(newSelectedSkus);
		}
	};

	return (
		<div className="tiled-title-list">
			{!isLoading &&
				(renderedSkus.length > 0 ? (
					<React.Fragment>
						{renderFilterLink(renderedSkus)}
						<List
							dataSource={sortTitles(renderedSkus)}
							pagination={{
								simple: true,
								pageSize: 20,
								position: 'both',
								hideOnSinglePage: true,
							}}
							renderItem={sku => (
								<div
									className={
										!isMobileSize ? 'two-column' : null
									}>
									{renderTitle(sku, titles)}
								</div>
							)}
						/>
						<div className="clear" />
					</React.Fragment>
				) : (
					<div className="no-content-message">
						<Translate id="no_titles" />
					</div>
				))}
		</div>
	);
};

const connectors = [connectToRequests, connectToDownloads, connectToReviews];

export default combineConnectors(connectors)(withIsMobile(TiledTitleList));

TiledTitleList.propTypes = {
	isMobile: PropTypes.func,
	isLoading: PropTypes.bool,
	skus: PropTypes.array,
	titles: PropTypes.array,
};
